/* =============================================================================
   CORE / MAIL-TEMPLATE-DEFAULTS — Lesbarer JS-Spiegel der Edge-Function-Defaults
   =============================================================================
   S82-4: Settings → Vorlagen zeigte bisher nur leere Override-Felder mit dem
   Hinweis „Leer lassen für den Standard-Text aus der Versand-Pipeline." — der
   Operator sah NICHT, was der Default ist. Dieser Spiegel macht die Defaults
   als read-only Preview sichtbar (Vorlagen-Editor), ohne sie zu duplizieren-
   und-vergessen.

   ⚠ ANTI-DRIFT (Memory `gg-edge-function-source-drift`) ⚠
   Single-Source der WAHRHEIT bleibt die Edge-Function
   `supabase/functions/process-mail-outbox/index.ts` → `KIND_TEMPLATES`
   (Stand v32). Dieser Spiegel ist NUR eine Anzeige-Kopie. Bei JEDER Änderung
   eines Default-Subjects/-Bodys in der Edge-Function MUSS dieser Spiegel
   nachgezogen werden — der Drift-Test `mail-template-defaults.test.js` liest
   die .ts-Source und schlägt fehl, sobald Spiegel und Source auseinanderlaufen.

   Hinweis Escaping: Die Edge-Function speichert Zeilenumbrüche als `\n`-Escape
   (on-disk `\\n`) und ersetzt sie beim Versand via `tpl.replace(/\\n/g,"\n")`.
   Hier stehen ECHTE Zeilenumbrüche (lesbar). Der Drift-Test vergleicht daher
   Zeile-für-Zeile als Substring gegen die Source (newline-agnostisch).

   Gemappt nach `settingsKey` → `settingsField` (exakt wie KIND_TEMPLATES sie
   in den settings-Slot schreibt). Belege (`receipt`/`correction`) + Exporte
   (`zbon`) haben KEINEN KIND_TEMPLATES-Eintrag — ihre Defaults leben in den
   JS-Quellen und werden hier PER REFERENZ gespiegelt (zero-drift); der
   .ts-Drift-Check schließt sie aus.
============================================================================ */

import { htmlEscape } from '../utils/helpers.js';
// S94: Belege (`receipt`/`correction`, JS-Direktpfad sendOrderReceiptEmail) +
// Exporte (`zbon`) haben KEINEN Edge-KIND_TEMPLATES-Eintrag — ihre Defaults
// leben in DEFAULT_ORDER_TEMPLATES / DEFAULT_EXPORT_TEMPLATES und werden hier
// PER REFERENZ gespiegelt (zero-drift, dieselbe Quelle). `received` wurde in S94
// entfernt (Eingangsbestätigung wird nicht mehr versendet).
import { DEFAULT_ORDER_TEMPLATES, DEFAULT_EXPORT_TEMPLATES } from './email-templates.js';

export const MAIL_TEMPLATE_DEFAULTS = {
  // ===== Bestellungen-Säule (emailTemplatesOrder) =====
  emailTemplatesOrder: {
    confirmed: {
      subject: 'Bestellung {{order_number}} bestätigt',
      body: 'Hallo {{name}},\n\ndeine Bestellung ist bestätigt!\n\nAbholung: {{pickup_date}} um {{pickup_time}} Uhr\nSumme: {{total}}\n\nDeine Positionen:\n{{items}}\n\nVerfolge deine Bestellung live:\n{{tracking_link}}\n\nFalls du die Bestellung stornieren möchtest:\n{{cancel_link}}\n\nWir freuen uns auf dich.\n\nDein {{brand}}-Team',
    },
    ready: {
      subject: 'Bestellung {{order_number}} ist fertig',
      body: 'Hallo {{name}},\n\ndeine Bestellung ist abholbereit!\n\nBis gleich bei uns.\n\nDeine Positionen:\n{{items}}\n\nStatus live:\n{{tracking_link}}\n\nDein {{brand}}-Team',
    },
    cancelled: {
      subject: 'Bestellung {{order_number}} storniert',
      body: 'Hallo {{name}},\n\ndeine Bestellung wurde storniert. Falls das ein Versehen war, melde dich gerne bei uns.\n\nDeine Positionen:\n{{items}}\n\nDein {{brand}}-Team',
    },
    // S85: Kassenbon (an den Kunden nach „Bezahlt"). JS-Quelle (resolveOrderTemplate
    // liest emailTemplatesOrder.receipt) — per Referenz gespiegelt, zero-drift.
    receipt: {
      subject: DEFAULT_ORDER_TEMPLATES.receipt.subject,
      body: DEFAULT_ORDER_TEMPLATES.receipt.body,
    },
    // S94: Stornobeleg (Korrekturbeleg). JS-Quelle (resolveOrderTemplate liest
    // emailTemplatesOrder.correction) — per Referenz gespiegelt, zero-drift.
    correction: {
      subject: DEFAULT_ORDER_TEMPLATES.correction.subject,
      body: DEFAULT_ORDER_TEMPLATES.correction.body,
    },
  },

  // ===== Exporte-Säule (emailTemplatesExport) — an Steuerberater/Buchhaltung =====
  // S85: JS-Quelle (DEFAULT_EXPORT_TEMPLATES), kein Edge-Eintrag. Per Referenz
  // gespiegelt, zero-drift; vom .ts-Drift-Check ausgenommen.
  emailTemplatesExport: {
    zbon: {
      subject: DEFAULT_EXPORT_TEMPLATES.zbon.subject,
      body: DEFAULT_EXPORT_TEMPLATES.zbon.body,
    },
  },

  // ===== Newsletter-Säule (emailTemplatesNewsletter, S226) — Double-Opt-in =====
  // Spiegel des Edge-KIND_TEMPLATES.newsletter_confirm (process-mail-outbox v47),
  // werbefrei, {{confirm_link}} → #newsletter-confirm/<token>.
  emailTemplatesNewsletter: {
    confirm: {
      subject: 'Bitte bestätige dein Newsletter-Abo bei {{brand}}',
      body: 'Hallo,\n\nschön, dass du unseren Newsletter abonnieren möchtest!\n\nBitte bestätige deine Anmeldung mit einem Klick — erst danach senden wir dir Neuigkeiten:\n{{confirm_link}}\n\nFalls du dich nicht angemeldet hast, ignoriere diese E-Mail einfach — dann passiert nichts.\n\nDein {{brand}}-Team',
    },
  },

  // ===== Team-Säule (emailTemplatesSchedule, S262) — Dienstplan-Zyklus =====
  // Spiegel des Edge-KIND_TEMPLATES.schedule_request / schedule_reminder
  // (process-mail-outbox v51). Interne Mitarbeiter-Mails, {{schedule_link}} →
  // #dienstplan/<token>, {{deadline}} in Wanduhrzeit des Restaurants.
  emailTemplatesSchedule: {
    request: {
      subject: 'Deine Verfügbarkeit für KW {{week_no}} ({{week_range}})',
      body: 'Hallo {{name}},\n\nbitte trag deine Verfügbarkeit für KW {{week_no}} ({{week_range}}) ein:\n{{schedule_link}}\n\nBis spätestens {{deadline}}. Bis dahin kannst du deine Angaben über denselben Link jederzeit ändern.\n\nWer nichts einträgt, bekommt in dieser Woche keine Schichten.\n\nDanke dir!\nDein {{brand}}-Team',
    },
    reminder: {
      subject: 'Erinnerung: deine Verfügbarkeit für KW {{week_no}} fehlt noch',
      body: 'Hallo {{name}},\n\nfür KW {{week_no}} ({{week_range}}) fehlt deine Verfügbarkeit noch. Bitte trag sie bis {{deadline}} ein:\n{{schedule_link}}\n\nOhne Eintrag können wir dich für diese Woche nicht einplanen — dann gibt es keine Schichten.\n\nDein {{brand}}-Team',
    },
    // S264 — Spiegel von KIND_TEMPLATES.schedule_published (v53). {{plan}} ist im
    // HTML eine Tabelle (Raw-Slot), im Text die Woche zeilenweise.
    published: {
      subject: 'Dienstplan KW {{week_no}} · v{{version}} ({{week_range}})',
      body: 'Hallo {{name}},\n\nhier ist der Dienstplan für KW {{week_no}} ({{week_range}}), Stand v{{version}}.\n\nDeine Schichten:\n{{my_shifts}}\n\nDie ganze Woche:\n{{plan}}\n\nDen Plan findest du auch als PDF im Anhang. Bei Fragen melde dich bitte direkt im Team.\n\nDein {{brand}}-Team',
    },
  },

  // ===== Lieferungen-Säule (emailTemplatesDelivery, S74 Variante C) =====
  emailTemplatesDelivery: {
    confirmed: {
      subject: 'Lieferung {{order_number}} bestätigt',
      body: 'Hallo {{name}},\n\ndeine Lieferung ist bestätigt!\n\nLieferzeit: {{pickup_date}} gegen {{pickup_time}} Uhr\nSumme: {{total}}\n\nDeine Positionen:\n{{items}}\n\nVerfolge deine Lieferung live:\n{{tracking_link}}\n\nFalls du die Lieferung stornieren möchtest:\n{{cancel_link}}\n\nUnser Fahrer macht sich rechtzeitig auf den Weg zu dir.\n\nDein {{brand}}-Team',
    },
    ready: {
      subject: 'Lieferung {{order_number}} ist unterwegs',
      body: 'Hallo {{name}},\n\ndeine Lieferung ist fertig und unser Fahrer ist auf dem Weg zu dir!\n\nDeine Positionen:\n{{items}}\n\nVerfolge die Lieferung live:\n{{tracking_link}}\n\nBitte halte dich bereit — wir kommen gleich an.\n\nDein {{brand}}-Team',
    },
    cancelled: {
      subject: 'Lieferung {{order_number}} storniert',
      body: 'Hallo {{name}},\n\ndeine Lieferung wurde storniert. Falls das ein Versehen war, melde dich gerne bei uns.\n\nDeine Positionen:\n{{items}}\n\nDein {{brand}}-Team',
    },
    // S213-B — Beleg an die „Zugestellt"-Mail (Uber-Stil). Der Edge-Versand hängt
    // das in S213-A gespeicherte Bon-PDF (bon_archive.pdf_path) als Anhang an.
    receipt: {
      subject: 'Deine Lieferung {{order_number}} ist da — dein Beleg',
      body: 'Hallo {{name}},\n\ndeine Lieferung ist da — guten Appetit und vielen Dank für deine Bestellung!\n\nSumme: {{total}}\n\nDeinen Beleg findest du als PDF im Anhang dieser Mail.\n\nDein {{brand}}-Team',
    },
  },

  // ===== Reservierungen-Säule (emailTemplates) =====
  emailTemplates: {
    confirmed: {
      subject: 'Reservierung am {{reservation_date}} bestätigt',
      body: 'Hallo {{name}},\n\ndeine Reservierung für {{guests}} Personen am {{reservation_date}} um {{reservation_time}} Uhr ist bestätigt.\n\nFalls sich deine Pläne ändern, kannst du hier stornieren:\n{{cancel_link}}\n\nWir freuen uns auf deinen Besuch!\n\nDein {{brand}}-Team',
    },
    declined: {
      subject: 'Reservierung am {{reservation_date}} — leider keine Verfügbarkeit',
      body: 'Hallo {{name}},\n\nleider können wir deine Reservierung für {{guests}} Personen am {{reservation_date}} um {{reservation_time}} Uhr nicht annehmen.\n\nWir würden uns freuen, wenn du es zu einem anderen Termin nochmal versuchst.\n\nDein {{brand}}-Team',
    },
    cancelled: {
      subject: 'Reservierung am {{reservation_date}} storniert',
      body: 'Hallo {{name}},\n\ndeine Reservierung für {{guests}} Personen am {{reservation_date}} um {{reservation_time}} Uhr wurde storniert.\n\nFalls das nicht beabsichtigt war oder du es dir anders überlegt hast, freuen wir uns auf eine neue Anfrage.\n\nDein {{brand}}-Team',
    },
    confirmed_with_order: {
      subject: 'Reservierung & Bestellung am {{reservation_date}} bestätigt',
      body: 'Hallo {{name}},\n\ndeine Reservierung für {{guests}} Personen am {{reservation_date}} um {{reservation_time}} Uhr ist bestätigt — inklusive deiner Vorbestellung.\n\n{{items_block}}\n\nGesamtsumme: {{total}}\n\nFalls sich deine Pläne ändern, kannst du hier stornieren:\n{{cancel_link}}\n\nWir freuen uns auf deinen Besuch!\n\nDein {{brand}}-Team',
    },
    reminder: {
      subject: 'Erinnerung: deine Reservierung am {{reservation_date}}',
      body: 'Hallo {{name}},\n\nkurze Erinnerung: wir freuen uns auf dich!\n\n  Datum: {{reservation_date}}\n  Zeit:  {{reservation_time}} Uhr\n  Gäste: {{guests}}\n\nFalls sich deine Pläne geändert haben, kannst du hier stornieren:\n{{cancel_link}}\n\nBis bald!\nDein {{brand}}-Team',
    },
  },
};

/** Default für einen Slot holen — null wenn kein Edge-Function-Default existiert
 *  (z.B. order_received). */
export function getMailTemplateDefault(settingsKey, field) {
  const grp = MAIL_TEMPLATE_DEFAULTS[settingsKey];
  if (!grp) return null;
  return grp[field] || null;
}

/** Read-only `<details>`-Preview-Block für einen Default. Liefert '' wenn kein
 *  Default existiert (dann zeigt das Feld nur den Override-Placeholder). Pure. */
export function buildDefaultPreviewHtml(settingsKey, field) {
  const def = getMailTemplateDefault(settingsKey, field);
  if (!def) return '';
  return `
    <details class="mail-default-preview" style="margin-top:6px;">
      <summary style="cursor:pointer; color:var(--ink-soft); font-size:.85rem;">📖 Standard-Text anzeigen</summary>
      <div style="margin-top:8px; padding:10px 12px; background:var(--bg-soft,#f6f6f6); border-radius:8px; font-size:.85rem;">
        <div style="color:var(--ink-soft); margin-bottom:4px;"><strong>Betreff:</strong> ${htmlEscape(def.subject)}</div>
        <pre style="white-space:pre-wrap; font-family:inherit; margin:0; color:var(--ink);">${htmlEscape(def.body)}</pre>
        <div style="color:var(--ink-muted,#999); font-size:.78rem; margin-top:8px;">
          Dieser Text wird automatisch verwendet, solange das Feld oben leer ist.
        </div>
      </div>
    </details>`;
}
